import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { useSelector } from 'react-redux';
import { db } from '@/shared/lib';
import { Store } from '@/redux';
import { LoggerService } from '@/shared/logger';

/**
 * Firestore collection layout
 * ---------------------------
 *   blocked_users / {blockerUID}_{blockedUID}
 *     {
 *       blockerUID:  string,
 *       blockedUID:  string,
 *       createdAt:   Timestamp (serverTimestamp),
 *     }
 *
 * The composite document id makes both "did A block B" lookups and the
 * delete-on-unblock operation O(1).
 *
 * SendMessage must check that *neither* direction is blocked before writing
 * to `chat`. Read-side hooks listen to both directions so the UI stays in
 * sync if the other party blocks me while the thread is open.
 */
export const BLOCKED_USERS_COLLECTION = 'blocked_users';

export const blockDocID = (blockerUID: string, blockedUID: string) =>
  `${blockerUID}_${blockedUID}`;

export interface BlockedUserRecord {
  blockerUID: string;
  blockedUID: string;
}

/** One-shot check used by send-message before posting to Firestore. */
export const isBlockedBetween = async (
  uidA: string,
  uidB: string
): Promise<{ aBlockedB: boolean; bBlockedA: boolean }> => {
  if (!uidA || !uidB) return { aBlockedB: false, bBlockedA: false };
  const [aBlockedB, bBlockedA] = await Promise.all([
    getDoc(doc(db, BLOCKED_USERS_COLLECTION, blockDocID(uidA, uidB))).then(
      d => d.exists()
    ),
    getDoc(doc(db, BLOCKED_USERS_COLLECTION, blockDocID(uidB, uidA))).then(
      d => d.exists()
    ),
  ]);
  return { aBlockedB, bBlockedA };
};

/**
 * Live two-way block status between the current user and `otherUID`.
 *
 * `iBlocked`  – true when *I* have blocked the other user
 * `blockedMe` – true when the other user has blocked *me*
 */
export const useIsBlocked = (otherUID?: string | null) => {
  const { firebaseUID } = useSelector((state: Store) => state.userReducer);
  const [iBlocked, setIBlocked] = useState(false);
  const [blockedMe, setBlockedMe] = useState(false);
  const [loading, setLoading] = useState<boolean>(!!firebaseUID && !!otherUID);

  useEffect(() => {
    if (!firebaseUID || !otherUID) {
      setIBlocked(false);
      setBlockedMe(false);
      setLoading(false);
      return;
    }
    setLoading(true);

    let pendingForward = true;
    let pendingReverse = true;

    const settle = () => {
      if (!pendingForward && !pendingReverse) setLoading(false);
    };

    const unsubForward = onSnapshot(
      doc(db, BLOCKED_USERS_COLLECTION, blockDocID(firebaseUID, otherUID)),
      snap => {
        setIBlocked(snap.exists());
        pendingForward = false;
        settle();
      },
      err => {
        LoggerService.error('blocked listener (forward) failed', err);
        pendingForward = false;
        settle();
      }
    );

    const unsubReverse = onSnapshot(
      doc(db, BLOCKED_USERS_COLLECTION, blockDocID(otherUID, firebaseUID)),
      snap => {
        setBlockedMe(snap.exists());
        pendingReverse = false;
        settle();
      },
      err => {
        LoggerService.error('blocked listener (reverse) failed', err);
        pendingReverse = false;
        settle();
      }
    );

    // Optimistic updates from in-app block/unblock actions (instant UI on iOS
    // where the snapshot can lag).
    const unsubOptimistic = subscribeBlockChange(({ blockerUID, blockedUID, kind }) => {
      if (blockerUID === firebaseUID && blockedUID === otherUID) {
        setIBlocked(kind === 'add');
      }
      if (blockerUID === otherUID && blockedUID === firebaseUID) {
        setBlockedMe(kind === 'add');
      }
    });

    return () => {
      unsubForward();
      unsubReverse();
      unsubOptimistic();
    };
  }, [firebaseUID, otherUID]);

  return { iBlocked, blockedMe, loading };
};

/**
 * Live list of all users *I* have blocked. Useful for the DM list page so
 * we can render a "blocked" badge per thread without N round-trips.
 */
export const useMyBlockedSet = (): { blockedSet: Set<string>; loading: boolean } => {
  const { firebaseUID } = useSelector((state: Store) => state.userReducer);
  const [blockedUIDs, setBlockedUIDs] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(!!firebaseUID);

  useEffect(() => {
    if (!firebaseUID) {
      setBlockedUIDs([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    const q = query(
      collection(db, BLOCKED_USERS_COLLECTION),
      where('blockerUID', '==', firebaseUID),
      // Required by the list rule: request.query.limit <= 200.
      limit(200)
    );
    const unsub = onSnapshot(
      q,
      snap => {
        const ids: string[] = [];
        snap.forEach(d => {
          const data = d.data() as BlockedUserRecord;
          if (data?.blockedUID) ids.push(data.blockedUID);
        });
        setBlockedUIDs(ids);
        setLoading(false);
      },
      err => {
        LoggerService.error('my-blocked listener failed', err);
        setLoading(false);
      }
    );

    const unsubOptimistic = subscribeBlockChange(({ blockerUID, blockedUID, kind }) => {
      if (blockerUID !== firebaseUID) return;
      setBlockedUIDs(prev => {
        const set = new Set(prev);
        if (kind === 'add') set.add(blockedUID);
        else set.delete(blockedUID);
        return Array.from(set);
      });
    });

    return () => {
      unsub();
      unsubOptimistic();
    };
  }, [firebaseUID]);

  return useMemo(
    () => ({ blockedSet: new Set(blockedUIDs), loading }),
    [blockedUIDs, loading]
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Optimistic-update bus
// ─────────────────────────────────────────────────────────────────────────────
// On iOS (Capacitor WebView) the Firestore JS SDK sometimes takes a noticeable
// moment to deliver the onSnapshot update after a local write — the UI then
// looks "stuck" on the old state until the user navigates away and back.
// To make block/unblock feel instant we publish a tiny in-app event whenever
// we mutate a block doc, and useIsBlocked() applies that hint to its local
// state right away. The Firestore snapshot still arrives eventually and is
// authoritative; if it disagrees, the snapshot wins.

type BlockChangeDetail = {
  blockerUID: string;
  blockedUID: string;
  // 'add' = blocker now blocks blocked, 'remove' = unblock
  kind: 'add' | 'remove';
};

const BLOCK_CHANGE_EVENT = 'bhabi:block-change';

const emitBlockChange = (detail: BlockChangeDetail) => {
  try {
    window.dispatchEvent(
      new CustomEvent<BlockChangeDetail>(BLOCK_CHANGE_EVENT, { detail })
    );
  } catch (err) {
    // SSR / non-browser env — safe to ignore.
    LoggerService.warn?.('block-change emit failed', err);
  }
};

/** Block another user. No-op if either UID is empty. */
export const blockUser = async (blockerUID: string, blockedUID: string) => {
  if (!blockerUID || !blockedUID || blockerUID === blockedUID) return;
  // Optimistic: announce the change before the round-trip so listeners can
  // update their local state instantly.
  emitBlockChange({ blockerUID, blockedUID, kind: 'add' });
  try {
    await setDoc(
      doc(db, BLOCKED_USERS_COLLECTION, blockDocID(blockerUID, blockedUID)),
      {
        blockerUID,
        blockedUID,
        createdAt: serverTimestamp(),
      }
    );
  } catch (err) {
    // Roll back the optimistic state if the write failed.
    emitBlockChange({ blockerUID, blockedUID, kind: 'remove' });
    throw err;
  }
};

/** Unblock a previously blocked user. Safe to call when no block exists. */
export const unblockUser = async (blockerUID: string, blockedUID: string) => {
  if (!blockerUID || !blockedUID) return;
  emitBlockChange({ blockerUID, blockedUID, kind: 'remove' });
  try {
    await deleteDoc(
      doc(db, BLOCKED_USERS_COLLECTION, blockDocID(blockerUID, blockedUID))
    );
  } catch (err) {
    // Roll back: the block still exists if the delete failed.
    emitBlockChange({ blockerUID, blockedUID, kind: 'add' });
    throw err;
  }
};

/**
 * Internal helper used by useIsBlocked and useMyBlockedSet to listen to
 * optimistic update events. Exported only for tests; not part of the public
 * API.
 */
export const subscribeBlockChange = (
  cb: (d: BlockChangeDetail) => void
): (() => void) => {
  const handler = (e: Event) => {
    const ev = e as CustomEvent<BlockChangeDetail>;
    if (ev.detail) cb(ev.detail);
  };
  window.addEventListener(BLOCK_CHANGE_EVENT, handler);
  return () => window.removeEventListener(BLOCK_CHANGE_EVENT, handler);
};
